import React, { useEffect, useReducer, useRef, useState } from 'react';
import { ImagePlus, Lock, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { postService } from '../services/postService';
import { cn } from '../util/designUtil';
import {
  initialPublishState,
  isPublishing,
  publishFailureMessage,
  publishReducer,
  publishStatusLabel,
} from '../util/postPublish';
import {
  ALLOWED_POST_MEDIA_TYPES,
  MAX_POST_CAPTION_LENGTH,
  PostMediaKind,
  formatMediaSize,
  postMediaKind,
  safePostFilename,
  validateNewPost,
  validatePostMedia,
} from '../util/postValidation';

interface PostComposerProps {
  creatorId: string;
}

/**
 * The creator's own post composer: pick a photo or a video, write a caption,
 * choose whether it is subscribers-only, then publish through the three-step
 * upload flow (upload URL, PUT the bytes, create the post). Nothing is written
 * to Firestore from here — the backend owns the post.
 */
const PostComposer: React.FC<PostComposerProps> = ({ creatorId }) => {
  const { theme } = useColorTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [mediaKind, setMediaKind] = useState<PostMediaKind | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [subscribersOnly, setSubscribersOnly] = useState(true);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [publishState, dispatch] = useReducer(publishReducer, initialPublishState);

  // Release the previous object URL when the choice changes and on unmount.
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (!creatorId) {
    return null;
  }

  const busy = isPublishing(publishState);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] ?? null;
    // Let the creator pick the same file again after a failure.
    event.target.value = '';

    if (!chosen) {
      return;
    }

    const result = validatePostMedia(chosen);
    if (!result.ok) {
      setFieldError(result.reason);
      return;
    }

    setFieldError(null);
    setFile(chosen);
    // Never null here: validatePostMedia just accepted the type.
    setMediaKind(postMediaKind(chosen.type));
    setPreviewUrl(URL.createObjectURL(chosen));
  };

  const resetComposer = () => {
    setFile(null);
    setMediaKind(null);
    setPreviewUrl(null);
    setCaption('');
    setSubscribersOnly(true);
    setFieldError(null);
  };

  const handlePublish = async () => {
    if (busy) {
      return;
    }

    const valid = validateNewPost(file, caption);
    if (!valid.ok) {
      setFieldError(valid.reason);
      toast.error(valid.reason);
      return;
    }
    if (!file) {
      return;
    }

    setFieldError(null);
    // The kind only changes the wording of a failure; the three steps below are
    // identical for a photo and a video.
    const kind: PostMediaKind = mediaKind ?? 'image';
    dispatch({ type: 'start' });

    // Step 1 — where to upload.
    const urlResult = await postService.requestUploadUrl({
      filename: safePostFilename(file.name),
      contentType: file.type,
    });
    if (!urlResult.success || !urlResult.target) {
      dispatch({ type: 'fail', at: 'requestingUrl', message: urlResult.message, kind });
      toast.error(publishFailureMessage('requestingUrl', urlResult.message, kind));
      return;
    }
    const target = urlResult.target;
    dispatch({ type: 'uploadUrlReady' });

    // Step 2 — the bytes, straight to the signed URL.
    const uploadResult = await postService.uploadImage(target, file, percent =>
      dispatch({ type: 'progress', percent }),
    );
    if (!uploadResult.success) {
      dispatch({ type: 'fail', at: 'uploading', message: uploadResult.message, kind });
      toast.error(publishFailureMessage('uploading', uploadResult.message, kind));
      return;
    }
    dispatch({ type: 'uploadComplete' });

    // Step 3 — the post itself.
    const createResult = await postService.createPost({
      postId: target.postId,
      caption: caption.trim(),
      gated: subscribersOnly,
      media: [{ path: target.path, contentType: file.type }],
    });
    if (!createResult.success) {
      dispatch({ type: 'fail', at: 'creating', message: createResult.message, kind });
      toast.error(publishFailureMessage('creating', createResult.message, kind));
      return;
    }

    dispatch({ type: 'postCreated' });
    toast.success('Post published');
    resetComposer();
  };

  return (
    <div className="w-full max-w-full bg-white p-5 sm:p-6 md:p-8 rounded-xl shadow-lg border border-gray-200">
      <div className="flex items-center gap-2 mb-1">
        <ImagePlus className={cn('h-5 w-5', theme.secondary)} />
        <h2 className="text-lg font-semibold text-gray-900">New post</h2>
      </div>
      <p className="text-sm text-gray-600">
        Share a photo or video with your fans. Subscribers-only posts are visible to fans on your monthly plan.
      </p>

      <div className="mt-4 space-y-4">
        <input
          type="file"
          ref={fileInputRef}
          accept={ALLOWED_POST_MEDIA_TYPES.join(',')}
          className="hidden"
          onChange={handleFileChange}
        />

        {previewUrl &&
          (mediaKind === 'video' ? (
            // A plain <video>, not Plyr: Plyr belongs to the read path and buys
            // nothing for a local object URL.
            <video
              src={previewUrl}
              controls
              playsInline
              data-testid="post-video-preview"
              className="w-full aspect-square object-cover rounded-lg border border-gray-200"
            />
          ) : (
            <img
              src={previewUrl}
              alt={file?.name || 'Selected'}
              className="w-full aspect-square object-cover rounded-lg border border-gray-200"
            />
          ))}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <ImagePlus className="h-4 w-4" />
            {file ? 'Change file' : 'Choose photo or video'}
          </button>
          <div className="min-w-0 flex-1 text-xs text-gray-500">
            {file ? (
              <>
                <span data-testid="post-file-name" className="block truncate">
                  {file.name}
                </span>
                <span data-testid="post-file-size" className="block">
                  {formatMediaSize(file.size)}
                </span>
              </>
            ) : (
              <span className="block">
                JPEG, PNG or WebP up to 10 MB, or MP4 or WebM up to 200 MB
              </span>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="postCaption" className="block text-xs font-medium text-gray-700 mb-1">
            Caption <span className="text-gray-500">(optional)</span>
          </label>
          <textarea
            id="postCaption"
            value={caption}
            onChange={e => {
              setCaption(e.target.value);
              setFieldError(null);
            }}
            disabled={busy}
            rows={3}
            maxLength={MAX_POST_CAPTION_LENGTH}
            placeholder="Say something about this post..."
            className="w-full min-w-0 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50 break-words"
          />
          <div
            className={cn(
              'text-xs text-right',
              caption.length > MAX_POST_CAPTION_LENGTH - 100 ? 'text-orange-500' : 'text-gray-400',
            )}
          >
            {caption.length}/{MAX_POST_CAPTION_LENGTH}
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={subscribersOnly}
            onChange={e => setSubscribersOnly(e.target.checked)}
            disabled={busy}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 disabled:opacity-50"
          />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
              <Lock className="h-4 w-4 text-gray-500" />
              Subscribers only
            </span>
            <span className="block text-xs text-gray-500 break-words">
              {subscribersOnly
                ? 'Only fans on your monthly plan can see this post.'
                : 'Anyone visiting your profile can see this post.'}
            </span>
          </span>
        </label>

        {busy && (
          <div>
            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div
                className={cn('h-full transition-all duration-200', theme.accent)}
                style={{ width: `${publishState.progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{publishStatusLabel(publishState)}</p>
          </div>
        )}

        {(fieldError || publishState.error) && (
          <p className="text-xs text-red-600 break-words">{fieldError || publishState.error}</p>
        )}

        <button
          type="button"
          onClick={handlePublish}
          disabled={!file || busy}
          className={cn(
            'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md text-sm font-semibold disabled:opacity-50',
            theme.accent,
          )}
        >
          {busy ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
              Publishing...
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Publish
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PostComposer;
